import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { GuidelinesHttpService, GuidelineSummary } from 'guideline/domain';
import { of, switchMap, tap } from 'rxjs';
import { map } from 'rxjs/operators';
import { InternalLinkSearchResult, LinksService, MessagesService } from 'shared/domain';
import { tapAllResponses } from 'shared/util-common';

import { LinkElementType, GuidelineState, Guideline } from '@treetop/shared/model';

@Injectable()
export class AnchorLinksSearchPopoverService {
  private messageService = inject(MessagesService);
  private linksService = inject(LinksService);
  private guidelinesHttpService = inject(GuidelinesHttpService);

  public loading = signal(false);

  public searchLinkResults = signal<any[]>([]);
  public searchGuidelineResults = signal<any[]>([]);

  public selectedGuideline = signal<Guideline | undefined>(undefined);

  private guideline: Guideline;

  public searchLinks = rxMethod<string>((searchTerm$) =>
    searchTerm$.pipe(
      tap((searchTerm) => {
        if (searchTerm.length < 3) {
          this.searchLinkResults.set([]);
          return;
        }

        this.loading.set(true);
        const links = this.linksService.searchForValueInGuideline(this.guideline, searchTerm);
        this.searchLinkResults.set(links);
      }),
      switchMap((searchTerm) => {
        if (searchTerm.length < 3) {
          return of({ list: [] });
        }

        return this.guidelinesHttpService.get(1, 1000, {
          filter: {
            searchText: searchTerm,
            guidelineStates: [GuidelineState.Published, GuidelineState.Approved, GuidelineState.InProgress, GuidelineState.InReview],
            showSharedGuidelines: true,
            showSoonToExpire: false,
            showExpired: false,
          },
        });
      }),
      map((response) => response.list),
      tapAllResponses<GuidelineSummary[], HttpErrorResponse>(
        (guidelines) => {
          this.loading.set(false);
          const mappedGuidelines: InternalLinkSearchResult[] = guidelines.map((guideline) => ({
            uniqueId: guideline.uniqueId,
            title: guideline.title,
            toElementType: LinkElementType.Guideline,
            toChapterUniqueId: null,
            toElementUniqueId: null,
            externalGuidelineUniqueId: guideline.uniqueId,
          }));
          this.searchGuidelineResults.set(mappedGuidelines);
        },
        (error: HttpErrorResponse) => {
          this.messageService.showError(undefined, error);
          this.loading.set(false);
        },
      ),
    ),
  );

  public getGuidelineDetails = rxMethod<string>((uniqueId$) =>
    uniqueId$.pipe(
      tap(() => {
        this.loading.set(true);
      }),
      switchMap((uniqueId) => this.guidelinesHttpService.getById(uniqueId)),
      tapAllResponses<Guideline, HttpErrorResponse>(
        (guideline) => {
          this.selectedGuideline.set(guideline);
          this.loading.set(false);
        },
        (error: HttpErrorResponse) => {
          this.messageService.showError(undefined, error);
          this.loading.set(false);
        },
      ),
    ),
  );

  public init(options: { guideline?: Guideline }) {
    this.guideline = options.guideline;
  }

  public clearSelectedGuideline(): void {
    this.selectedGuideline.set(undefined);
  }
}
